import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';

const QuizPerformanceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to navigate back to the appropriate dashboard
  const navigateBackToDashboard = () => {
    if (user?.role === 'admin') {
      navigate('/admin/dashboard/performance');
    } else if (user?.role === 'teacher') {
      navigate('/teachers/dashboard/performance');
    } else {
      // Fallback to browser history if role is unclear
      navigate(-1);
    }
  };

  // Function to export quiz performance data
  const exportQuizData = async (format = 'csv') => {
    try {
      const response = await api.get(`/performance/export?quizIds=${id}&format=${format}`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Set filename based on format
      const timestamp = new Date().toISOString().split('T')[0];
      const quizTitle = quizInfo?.title?.replace(/[^a-zA-Z0-9]/g, '_') || 'quiz';
      a.download = `${quizTitle}_performance_${timestamp}.${format}`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data. Please try again.');
    }
  };

  // Function to export data as JSON (client-side)
  const exportAsJSON = () => {
    const exportData = {
      quiz: quizInfo,
      statistics: summary,
      students: studentRecords,
      exportedAt: new Date().toISOString(),
      exportedBy: user?.name || 'Unknown'
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    const timestamp = new Date().toISOString().split('T')[0];
    const quizTitle = quizInfo?.title?.replace(/[^a-zA-Z0-9]/g, '_') || 'quiz';
    a.download = `${quizTitle}_performance_${timestamp}.json`;
    
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Function to export data as CSV (client-side)
  const exportAsCSV = () => {
    if (!studentRecords || studentRecords.length === 0) {
      alert('No student data to export');
      return;
    }

    // CSV headers
      const headers = [
      'Student Name',
      'Student Type',
        ...(quizInfo?.isRanked ? ['Rank'] : []),
      'Score',
      'Attempts',
      'Last Attempt',
      'Status',
      'Email'
    ];

    // Convert data to CSV rows
    const csvRows = [
      headers.join(','),
      ...studentRecords.map(record => [
        `"${record.studentName || ''}"`,
        `"${record.studentType || 'N/A'}"`,
        ...(quizInfo?.isRanked ? [`"${record.rank ? `#${record.rank}${record.rankTotal ? ` / ${record.rankTotal}` : ''}` : 'N/A'}"`] : []),
        record.correct && record.total ? `${record.correct}/${record.total}` : `${Math.round(record.score)}%`,
        record.attempts || 1,
        record.lastAttempt ? new Date(record.lastAttempt).toLocaleDateString() : 'N/A',
        quizInfo?.passingScore ? (record.score >= quizInfo.passingScore ? 'Passed' : 'Failed') : 'N/A',
        `"${record.email || 'N/A'}"`
      ].join(','))
    ];

    // Add quiz information at the top
    const quizInfoRows = [
      `Quiz: "${quizInfo?.title || 'Unknown'}"`,
      `Course: "${quizInfo?.course || 'Unknown'}"`,
      `Total Students: ${summary?.totalStudents || 0}`,
      `Average Score: ${summary?.averageScore || 0}`,
      `Highest Score: ${summary?.highestScore || 0}`,
      `Lowest Score: ${summary?.lowestScore || 0}`,
      `Pass Rate: ${summary?.passRate || 'N/A'}`,
      `Export Date: ${new Date().toLocaleDateString()}`,
      '', // Empty row
      ...csvRows
    ];

    const csvContent = quizInfoRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    const timestamp = new Date().toISOString().split('T')[0];
    const quizTitle = quizInfo?.title?.replace(/[^a-zA-Z0-9]/g, '_') || 'quiz';
    a.download = `${quizTitle}_performance_${timestamp}.csv`;
    
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const fetchQuizDetails = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/performance/quiz-details/${id}`);
        setPerformanceData(response.data);
      } catch (err) {
        console.error('Error fetching quiz performance details:', err);
        setError(err.response?.data?.error || err.message || 'Failed to load quiz performance details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchQuizDetails();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-gray-700 mx-auto" />
            <p className="mt-4 text-sm text-gray-600">Loading quiz performance...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white border border-red-200 rounded-md p-6 mt-8">
            <h2 className="text-xl font-semibold text-red-700 mb-2">Error loading performance details</h2>
            <p className="text-red-600">{error}</p>
            <button
              onClick={navigateBackToDashboard}
              className="mt-4 px-4 py-2 bg-gray-800 text-white rounded text-sm hover:bg-gray-900"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!performanceData) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white border border-yellow-200 rounded-md p-6 mt-8">
            <h2 className="text-xl font-semibold text-yellow-800 mb-2">No data available</h2>
            <p className="text-yellow-700">No performance data found for this quiz.</p>
            <button
              onClick={navigateBackToDashboard}
              className="mt-4 px-4 py-2 bg-gray-800 text-white rounded text-sm hover:bg-gray-900"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { quiz: quizInfo, statistics: summary, students: studentRecords } = performanceData;

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="bg-white border border-gray-200 rounded-md p-5">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">{quizInfo?.title}</h1>
              <p className="text-sm text-gray-600 mt-1">{quizInfo?.course}</p>
              {quizInfo?.isRanked && (
                <div className="mt-3 inline-flex items-center px-2.5 py-1 rounded border border-yellow-200 bg-yellow-50 text-yellow-800 text-xs font-medium">
                  Ranking enabled
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={exportAsCSV}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                Export CSV
              </button>
              <button
                onClick={exportAsJSON}
                className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
              >
                Export JSON
              </button>
              <button
                onClick={() => exportQuizData('excel')}
                className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
              >
                Export Excel
              </button>
              <button
                onClick={navigateBackToDashboard}
                className="px-4 py-2 bg-gray-800 text-white rounded text-sm hover:bg-gray-900"
              >
                Back to dashboard
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quiz information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-50 border border-gray-200 rounded p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Time Limit</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {quizInfo?.timeLimit ? `${quizInfo.timeLimit} min` : '∞'}
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Max Attempts</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {quizInfo?.maxAttempts || '∞'}
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Passing Score</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {quizInfo?.passingScore || 'N/A'}
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Status</p>
              <p className="text-2xl font-semibold text-gray-900 capitalize mt-1">
                {quizInfo?.status || 'Active'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance summary</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-gray-50 border border-gray-200 rounded p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Total Students</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{summary?.totalStudents || 0}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Average Score</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{summary?.averageScore || 0}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Highest Score</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{summary?.highestScore || 0}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Lowest Score</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{summary?.lowestScore || 0}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Pass Rate</p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {summary?.passRate || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
          <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-lg font-semibold text-gray-900">Student performance</h2>
              {studentRecords && studentRecords.length > 0 && (
                <button
                  onClick={exportAsCSV}
                  className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                >
                  Export CSV
                </button>
              )}
            </div>
          </div>
          {studentRecords && studentRecords.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Student Type
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Score
                    </th>
                    {quizInfo?.isRanked && (
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Rank
                      </th>
                    )}
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Attempts
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Last Attempt
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {studentRecords.map((record, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{record.studentName}</div>
                          <div className="text-xs text-gray-500">{record.email || 'N/A'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-3 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-300">
                          {record.studentType || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-semibold ${
                          record.score >= 80 ? 'bg-green-100 text-green-800' :
                          record.score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {record.correct && record.total ? `${record.correct}/${record.total}` : `${Math.round(record.score)}%`}
                        </span>
                      </td>
                      {quizInfo?.isRanked && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          {record.rank ? (
                            <span className="inline-flex items-center px-3 py-1.5 rounded-md bg-purple-100 text-purple-800 text-sm font-semibold">
                              #{record.rank}{record.rankTotal ? ` / ${record.rankTotal}` : ''}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">N/A</span>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded text-sm font-semibold">
                          {record.attempts || 1} attempt{(record.attempts || 1) !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {record.lastAttempt ? new Date(record.lastAttempt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {quizInfo?.passingScore && (
                          <span className={`inline-flex items-center px-3 py-1.5 rounded text-sm font-semibold ${
                            record.score >= quizInfo.passingScore
                              ? 'bg-emerald-50 text-emerald-800'
                              : 'bg-rose-50 text-rose-800'
                          }`}>
                            {record.score >= quizInfo.passingScore ? 'Passed' : 'Failed'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-700 font-medium">No student records found.</p>
              <p className="text-gray-500 text-sm mt-1">No students have attempted this quiz yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizPerformanceDetailPage;
